import React, { useEffect, useMemo, useState } from 'react';
import { SliderModal } from '@components/molecules/SlideOver';
import { Avatar } from '@components/atoms/Avatar';
import { Loading } from '@components/atoms/Loading';
import { Tab, TabItem } from '@components/atoms/Tab';
import { SearchInput } from '@components/molecules/SearchInput';
import { Accordion } from '@components/molecules/Accordion';
import PunchSessionCard from '@components/molecules/PunchSessionCard';
import { usePunchClocksByUser } from '@hooks/api/punchClock';
import { usePunchSessionAdjustmentsByUser } from '@hooks/api/punchSessionAdjustment';
import { Employee } from '@models/employee.model';
import { PunchClock, PunchClockAction } from '@models/punchClock.model';
import { PunchSessionAdjustment } from '@models/punchSessionAdjustment.model';
import { UserRoles } from '@models/user.model';
import { useAppSelector } from '@redux/hooks';
import { getDateSectionHeader, formatDateOnly } from '@utils/formatDate';
import { EditSessionExitModal } from './EditSessionExitModal';
import HistoryIcon from '@mui/icons-material/History';
import RefreshIcon from '@mui/icons-material/Refresh';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

interface UserPunchHistoryDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  employee: Employee | null;
}

type Period = 'TODAY' | 'WEEK' | 'MONTH' | 'ALL';

const PERIOD_TABS: TabItem[] = [
  { id: 'TODAY', name: 'Today' },
  { id: 'WEEK', name: 'Week' },
  { id: 'MONTH', name: 'Month' },
  { id: 'ALL', name: 'All' },
];

const STATIC_PERIOD_LABEL: Record<Exclude<Period, 'MONTH'>, string> = {
  TODAY: 'today',
  WEEK: 'this week',
  ALL: 'all time',
};

interface MonthRef {
  year: number;
  month: number; // 0-11
}

const currentMonthRef = (): MonthRef => {
  const now = new Date();
  return { year: now.getFullYear(), month: now.getMonth() };
};

const isSameMonth = (a: MonthRef, b: MonthRef): boolean =>
  a.year === b.year && a.month === b.month;

// Turn backend enum-style strings (BI_WEEKLY, HOURLY) into a label the user
// recognizes ("Bi weekly", "Hourly"). Lossless enough for status display.
const humanize = (raw: string): string => {
  const lower = raw.replace(/_/g, ' ').toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
};

const formatMonthLabel = (m: MonthRef): string =>
  new Date(m.year, m.month, 1).toLocaleString('en-US', {
    month: 'long',
    year: 'numeric',
  });

const addMonths = (m: MonthRef, delta: number): MonthRef => {
  const d = new Date(m.year, m.month + delta, 1);
  return { year: d.getFullYear(), month: d.getMonth() };
};

// Cap the "All" view so a long-tenured user doesn't render thousands of rows.
const ALL_CAP = 200;

// Initial number of days rendered, then "Load more" reveals another batch.
const DAYS_PAGE_SIZE = 15;

const NO_LOCATION_KEY = '__no_location__';

// Roles allowed to correct session hours. Mirrors the backend @HasPermission
// gate on editPunchSessionExit — the button is hidden for everyone else and the
// mutation is rejected server-side regardless.
const HOURS_EDIT_ROLES: string[] = [
  UserRoles.ADMIN,
  UserRoles.OWNER,
  UserRoles.PROJECT_MANAGER,
  UserRoles.SUPERINTENDENT,
];

// Group key for a punch — projects win, but warehouse-only geofences (which
// have no projectId) fall back to the geofence itself so the user sees
// "Main Warehouse" instead of "Unknown project".
const getLocationKey = (p: PunchClock): string => {
  if (p.project?.projectId) return `p:${p.project.projectId}`;
  if (p.geofence?.geofenceId) return `g:${p.geofence.geofenceId}`;
  return NO_LOCATION_KEY;
};

const getLocationName = (p: PunchClock): string =>
  p.project?.name ?? p.geofence?.name ?? 'Unknown location';

// Surfaced under the location name in the accordion header so the user can
// see where the work was done at a glance. Only project-backed punches
// expose an address; warehouse-only geofences leave it null.
const getLocationAddress = (p: PunchClock): string | null =>
  p.project?.address ?? null;

interface Session {
  /** The ENTER punch that anchors this session — the edit unit. */
  enterPunchClockId: string;
  /**
   * The real EXIT punch that closes this session, if any. Null for an open
   * session or one closed by an anomaly (ENTER-after-ENTER). When present, the
   * exit is edited in-place from that EXIT row; when null, there's no EXIT row
   * so entry + exit are edited together from the ENTER row.
   */
  exitPunchClockId: string | null;
  /** Effective entry: the correction if edited, else the raw ENTER time. */
  from: Date;
  /** Raw ENTER time — the pre-edit reference shown in the modal. */
  rawFrom: Date;
  /** Effective exit: the correction if edited, else the raw exit, else now. */
  to: Date;
  /** Raw paired exit (null for an open session) — the pre-edit reference. */
  rawTo: Date | null;
  isOpen: boolean;
  /** True when a manual correction overrides the raw entry and/or exit. */
  edited: boolean;
  locationKey: string;
  locationName: string;
  locationAddress: string | null;
  /** The ENTER punch anchoring this session — source of project/geofence. */
  enterPunch: PunchClock;
}

const dateKey = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate(),
  ).padStart(2, '0')}`;

const startOfDay = (d: Date): Date =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate());

const todayKey = (): string => dateKey(new Date());

// Calendar windows. "Week" = Monday → Sunday of the current week (ISO style).
// "Month" = first → last calendar day of the selected month (defaults to the
// current month, but the user can navigate back via the month selector).
const getPeriodRange = (
  period: Period,
  monthRef: MonthRef,
): { from: Date; to: Date } | null => {
  if (period === 'ALL') return null;
  const now = new Date();
  const today = startOfDay(now);
  if (period === 'TODAY') {
    const to = new Date(today);
    to.setDate(to.getDate() + 1);
    to.setMilliseconds(-1);
    return { from: today, to };
  }
  if (period === 'WEEK') {
    const dow = today.getDay(); // 0=Sun … 6=Sat
    const mondayOffset = (dow + 6) % 7;
    const monday = new Date(today);
    monday.setDate(today.getDate() - mondayOffset);
    const sundayEnd = new Date(monday);
    sundayEnd.setDate(monday.getDate() + 7);
    sundayEnd.setMilliseconds(-1);
    return { from: monday, to: sundayEnd };
  }
  // MONTH — uses the user-selected month.
  const first = new Date(monthRef.year, monthRef.month, 1);
  const next = new Date(monthRef.year, monthRef.month + 1, 1);
  next.setMilliseconds(-1);
  return { from: first, to: next };
};

// Walk a chronologically-sorted list of punches, pairing each ENTER with the
// next EXIT. An ENTER followed by another ENTER (rare anomaly — user moved
// between geofences without an EXIT) closes the previous session at the new
// ENTER's timestamp. A trailing ENTER without EXIT is reported as an open
// session up to "now". The session inherits its location from its ENTER.
const computeSessions = (
  punches: PunchClock[],
  adjustmentByEnterId: Map<string, PunchSessionAdjustment>,
): Session[] => {
  const sorted = [...punches].sort(
    (a, b) => new Date(a.time).getTime() - new Date(b.time).getTime(),
  );
  const sessions: Session[] = [];
  let active: {
    enterId: string;
    enter: Date;
    locationKey: string;
    locationName: string;
    locationAddress: string | null;
    enterPunch: PunchClock;
  } | null = null;

  // Close the active session at `rawExit` (null for a still-open trailing
  // session). If the session's ENTER has a manual correction, the corrected
  // exit substitutes the raw one — so every downstream day/location/period
  // total reconciles without any special-casing.
  const pushSession = (rawExit: Date | null, exitPunchClockId: string | null): void => {
    if (!active) return;
    const adjustment = adjustmentByEnterId.get(active.enterId);
    // A correction substitutes both the entry and the exit; totals downstream
    // recompute off session.from / session.to with no special-casing.
    const from = adjustment ? new Date(adjustment.editedEnterTime) : active.enter;
    const to = adjustment ? new Date(adjustment.editedExitTime) : rawExit ?? new Date();
    sessions.push({
      enterPunchClockId: active.enterId,
      exitPunchClockId,
      from,
      rawFrom: active.enter,
      to,
      rawTo: rawExit,
      // An edited open session is now closed by its correction.
      isOpen: rawExit === null && !adjustment,
      edited: !!adjustment,
      locationKey: active.locationKey,
      locationName: active.locationName,
      locationAddress: active.locationAddress,
      enterPunch: active.enterPunch,
    });
  };

  for (const p of sorted) {
    const t = new Date(p.time);
    if (p.action === PunchClockAction.ENTER) {
      // ENTER after an unclosed ENTER closes the previous session, but there's
      // no real EXIT punch for it (exitPunchClockId = null).
      pushSession(active ? t : null, null);
      active = {
        enterId: p.punchClockId,
        enter: t,
        locationKey: getLocationKey(p),
        locationName: getLocationName(p),
        locationAddress: getLocationAddress(p),
        enterPunch: p,
      };
    } else if (active) {
      // Real EXIT punch closes the session — record it so the exit is editable
      // in-place from this EXIT row.
      pushSession(t, p.punchClockId);
      active = null;
    }
  }
  pushSession(null, null);
  return sessions;
};

// A session's duration in minutes. A session counts fully on its entry day —
// simpler than splitting across midnight and consistent with the card readout.
// Clamped so a malformed adjustment (exit before entry, from data authored
// elsewhere) can never drag a day/period total below the true sum.
const sessionMinutes = (session: Session): number =>
  Math.max(0, (session.to.getTime() - session.from.getTime()) / 60000);

const formatMinutes = (mins: number): string => {
  const total = Math.max(0, Math.floor(mins));
  if (total < 1) return '0m';
  const h = Math.floor(total / 60);
  const m = total % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
};

interface DaySessionGroup {
  dateKey: string;
  dateHeader: string;
  dateObj: Date;
  totalMinutes: number;
  hasOpenSession: boolean;
  sessions: Session[];
}

export const UserPunchHistoryDrawer: React.FC<UserPunchHistoryDrawerProps> = ({
  isOpen,
  onClose,
  employee,
}) => {
  const [period, setPeriod] = useState<Period>('WEEK');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMonth, setSelectedMonth] = useState<MonthRef>(currentMonthRef);
  // Client-side pagination by days — keeps initial render light when a
  // long-tenured user has months of history.
  const [displayedDayCount, setDisplayedDayCount] = useState(DAYS_PAGE_SIZE);
  // The session being corrected (null = modal closed). One "Edit hours" button
  // per card opens the modal against the whole session.
  const [editSession, setEditSession] = useState<Session | null>(null);

  const userId = employee?.user?.userId ?? null;

  const role = useAppSelector((state) => state.user.role);
  const canEdit = !!role && HOURS_EDIT_ROLES.includes(role);

  // Reset state every time we open for a different user.
  useEffect(() => {
    if (!isOpen) return;
    setPeriod('WEEK');
    setSearchQuery('');
    setSelectedMonth(currentMonthRef());
    setDisplayedDayCount(DAYS_PAGE_SIZE);
    setEditSession(null);
  }, [isOpen, userId]);

  // Reset pagination whenever the visible window changes — otherwise jumping
  // from "ALL" back to "WEEK" would inherit a stale offset.
  useEffect(() => {
    setDisplayedDayCount(DAYS_PAGE_SIZE);
  }, [period, searchQuery, selectedMonth]);

  // Switching to the Month tab snaps the navigator to the current month —
  // matches the spec: "default to current month" each time the user
  // engages with the month filter.
  const handlePeriodChange = (next: Period) => {
    setPeriod(next);
    if (next === 'MONTH') setSelectedMonth(currentMonthRef());
  };

  const isCurrentMonth = useMemo(
    () => isSameMonth(selectedMonth, currentMonthRef()),
    [selectedMonth],
  );

  const { data: punches = [], isLoading, isFetching, refetch, dataUpdatedAt } =
    usePunchClocksByUser(userId, null, { enabled: isOpen });

  const { data: adjustments = [] } = usePunchSessionAdjustmentsByUser(userId, {
    enabled: isOpen,
  });

  // enterPunchClockId → its correction. Load-bearing: feeds both the session
  // math (exit substitution) and the per-row "Edited" badge/affordance.
  const adjustmentByEnterId = useMemo(() => {
    const map = new Map<string, PunchSessionAdjustment>();
    adjustments.forEach((a) => map.set(a.enterPunchClockId, a));
    return map;
  }, [adjustments]);

  const range = useMemo(
    () => getPeriodRange(period, selectedMonth),
    [period, selectedMonth],
  );

  // Sessions are computed from the FULL dataset so cross-period sessions
  // are paired correctly even when the period filter would hide their
  // counterpart. Corrections substitute their session's exit here.
  const sessions = useMemo(
    () => computeSessions(punches, adjustmentByEnterId),
    [punches, adjustmentByEnterId],
  );

  // A session is "in range" when its ENTRY falls inside the selected window.
  // A session counts fully on its entry day (no split across midnight).
  const inRange = useMemo(() => {
    return (t: Date): boolean => {
      if (!range) return true;
      return t.getTime() >= range.from.getTime() && t.getTime() <= range.to.getTime();
    };
  }, [range]);

  // Filter sessions by period range + search, then group by entry day.
  const dayGroups = useMemo<DaySessionGroup[]>(() => {
    const q = searchQuery.trim().toLowerCase();
    const matchesSearch = (s: Session): boolean =>
      !q || s.locationName.toLowerCase().includes(q);

    let visible = sessions.filter((s) => inRange(s.from) && matchesSearch(s));

    // Cap "All" to the most recent sessions so a long-tenured user doesn't
    // render an unbounded list.
    if (period === 'ALL' && visible.length > ALL_CAP) {
      visible = [...visible]
        .sort((a, b) => b.from.getTime() - a.from.getTime())
        .slice(0, ALL_CAP);
    }

    const byDay = new Map<string, Session[]>();
    for (const s of visible) {
      const dKey = dateKey(s.from);
      if (!byDay.has(dKey)) byDay.set(dKey, []);
      byDay.get(dKey)!.push(s);
    }

    const groups: DaySessionGroup[] = Array.from(byDay.entries()).map(
      ([dKey, ss]) => {
        // Ascending within the day — reads as the user's actual workday flow.
        const sorted = [...ss].sort((a, b) => a.from.getTime() - b.from.getTime());
        const totalMinutes = sorted.reduce((acc, s) => acc + sessionMinutes(s), 0);
        return {
          dateKey: dKey,
          dateHeader: getDateSectionHeader(sorted[0].from),
          dateObj: startOfDay(sorted[0].from),
          totalMinutes,
          hasOpenSession: sorted.some((s) => s.isOpen),
          sessions: sorted,
        };
      },
    );

    // Newest day first.
    groups.sort((a, b) => b.dateObj.getTime() - a.dateObj.getTime());
    return groups;
  }, [sessions, inRange, searchQuery, period]);

  const visibleSessionCount = useMemo(
    () => dayGroups.reduce((acc, g) => acc + g.sessions.length, 0),
    [dayGroups],
  );

  // Period total = sum of session durations for sessions whose entry is in
  // range (search-agnostic, matching the old totals behavior).
  const periodMinutes = useMemo(
    () =>
      sessions
        .filter((s) => inRange(s.from))
        .reduce((acc, s) => acc + sessionMinutes(s), 0),
    [sessions, inRange],
  );

  const enterCount = punches.filter(
    (p) => p.action === PunchClockAction.ENTER,
  ).length;
  const exitCount = punches.length - enterCount;

  const lastRefreshedLabel = useMemo(() => {
    if (!dataUpdatedAt) return null;
    const date = new Date(dataUpdatedAt);
    return date.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  }, [dataUpdatedAt]);

  const fullName = employee?.user
    ? `${employee.user.firstName ?? ''} ${employee.user.lastName ?? ''}`.trim() ||
      employee.user.email ||
      'User'
    : 'User';

  // Compact employment details surfaced in the header so a manager can see
  // pay context (rate / mode / period) and key dates without leaving the drawer.
  const detailRows = useMemo(() => {
    if (!employee) return [];
    const rows: { label: string; value: string }[] = [];
    if (employee.rate != null) {
      const rate = `$${Number(employee.rate).toFixed(2)}`;
      rows.push({ label: 'Rate', value: rate });
    }
    if (employee.mode) {
      rows.push({ label: 'Mode', value: humanize(employee.mode) });
    }
    if (employee.payPeriod) {
      rows.push({ label: 'Pay period', value: humanize(employee.payPeriod) });
    }
    if (employee.dob) {
      rows.push({ label: 'DOB', value: formatDateOnly(employee.dob) });
    }
    if (employee.hireDate) {
      rows.push({
        label: 'Hire date',
        value: formatDateOnly(employee.hireDate),
      });
    }
    return rows;
  }, [employee]);

  const title = (
    <div className="flex items-center gap-2">
      <HistoryIcon className="text-orange-400" sx={{ fontSize: 24 }} />
      <span>Activity history</span>
    </div>
  );

  const emptyLabel = useMemo(() => {
    if (searchQuery.trim()) return 'No events match this search.';
    if (period === 'TODAY') return 'No events recorded today.';
    if (period === 'WEEK') return 'No events recorded this week.';
    if (period === 'MONTH') {
      return isCurrentMonth
        ? 'No events recorded this month.'
        : `No events recorded in ${formatMonthLabel(selectedMonth)}.`;
    }
    return 'No punch events recorded for this user yet.';
  }, [searchQuery, period, isCurrentMonth, selectedMonth]);

  const periodLabel = useMemo(() => {
    if (period !== 'MONTH') return STATIC_PERIOD_LABEL[period];
    return isCurrentMonth ? 'this month' : formatMonthLabel(selectedMonth);
  }, [period, isCurrentMonth, selectedMonth]);

  // Default-expand "today" when it's in the visible range. When the user
  // navigates to a past month (where today isn't part of the range) we fall
  // back to expanding the newest day of that group so the drawer never
  // opens fully collapsed.
  const todaysKey = todayKey();
  const defaultExpandedKey = useMemo(() => {
    if (dayGroups.some((g) => g.dateKey === todaysKey)) return todaysKey;
    return dayGroups[0]?.dateKey ?? null;
  }, [dayGroups, todaysKey]);

  return (
    <SliderModal
      isOpen={isOpen}
      closeSlider={onClose}
      title={title}
      cancelText="Close"
    >
      {employee && (
        <div className="bg-gray-50 rounded-lg p-3 mb-4">
          <div className="flex items-start gap-3">
            <Avatar
              size="lg"
              src={employee.user?.avatar || undefined}
              text={employee.user?.firstName}
              alt={fullName}
            />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-gray-900 truncate">
                {fullName}
              </p>
              {employee.user?.email && (
                <p className="text-xs text-gray-500 truncate">
                  {employee.user.email}
                </p>
              )}
              {employee.user?.role && (
                <span className="inline-flex mt-1 items-center px-2 py-0.5 text-[10px] font-semibold rounded-full bg-indigo-50 text-indigo-700 uppercase tracking-wide">
                  {employee.user.role}
                </span>
              )}
            </div>
            <div className="flex flex-col items-end text-xs text-gray-500 flex-shrink-0">
              <span>
                <span className="font-semibold text-gray-700">{enterCount}</span> in
              </span>
              <span>
                <span className="font-semibold text-gray-700">{exitCount}</span> out
              </span>
            </div>
          </div>

          {detailRows.length > 0 && (
            <div className="mt-3 pt-3 border-t border-gray-200 grid grid-cols-2 gap-x-3 gap-y-1.5">
              {detailRows.map((row) => (
                <div
                  key={row.label}
                  className="flex items-center justify-between gap-2 min-w-0">
                  <span className="text-[11px] font-medium text-gray-500 uppercase tracking-wide flex-shrink-0">
                    {row.label}
                  </span>
                  <span className="text-xs font-semibold text-gray-800 truncate">
                    {row.value}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="flex flex-col gap-3 mb-3">
        <Tab
          tabs={PERIOD_TABS}
          activeTab={period}
          onTabChange={(id) => handlePeriodChange(id as Period)}
          variant="pills"
          size="sm"
          fullWidth
        />

        {period === 'MONTH' ? (
          <div className="grid grid-cols-2 gap-2">
            {/* Border + padding mirror SearchInput so both columns render at
                the same height. */}
            <div className="flex items-center justify-between gap-1 border border-gray-300 rounded-md py-1 px-1.5 bg-white">
              <button
                type="button"
                onClick={() => setSelectedMonth((m) => addMonths(m, -1))}
                className="w-7 h-7 rounded-md flex items-center justify-center text-gray-600 hover:bg-gray-100 flex-shrink-0"
                aria-label="Previous month"
              >
                <ChevronLeftIcon sx={{ fontSize: 18 }} />
              </button>
              <div className="flex items-center gap-1.5 min-w-0">
                <span className="text-sm font-semibold text-gray-900 truncate">
                  {formatMonthLabel(selectedMonth)}
                </span>
                {!isCurrentMonth && (
                  <button
                    type="button"
                    onClick={() => setSelectedMonth(currentMonthRef())}
                    className="text-[11px] font-medium text-indigo-600 hover:text-indigo-700 flex-shrink-0"
                    title="Go to current month"
                  >
                    This month
                  </button>
                )}
              </div>
              <button
                type="button"
                onClick={() => setSelectedMonth((m) => addMonths(m, 1))}
                disabled={isCurrentMonth}
                className="w-7 h-7 rounded-md flex items-center justify-center text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent flex-shrink-0"
                aria-label="Next month"
              >
                <ChevronRightIcon sx={{ fontSize: 18 }} />
              </button>
            </div>

            <SearchInput
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder="Search project or geofence..."
              className="!w-full"
            />
          </div>
        ) : (
          <SearchInput
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Search by project or geofence..."
            className="!w-full"
          />
        )}
      </div>

      {/* Period total — the headline number for the selected window. */}
      <div className="bg-indigo-50 rounded-lg ring-1 ring-indigo-100 p-3 mb-3 flex items-center gap-3">
        <div className="w-9 h-9 rounded-md bg-white flex items-center justify-center text-indigo-600 flex-shrink-0">
          <TimerOutlinedIcon sx={{ fontSize: 20 }} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-medium text-indigo-700/80 uppercase tracking-wide">
            Total time in zones · {periodLabel}
          </p>
          <p className="text-lg font-semibold text-indigo-900 leading-tight">
            {formatMinutes(periodMinutes)}
          </p>
        </div>
      </div>

      <div className="flex items-center justify-between mb-3">
        <p className="text-xs text-gray-500">
          {visibleSessionCount}{' '}
          {visibleSessionCount === 1 ? 'session' : 'sessions'}
          {period === 'ALL' && sessions.length > ALL_CAP && (
            <span className="text-gray-400"> · showing last {ALL_CAP}</span>
          )}
          {lastRefreshedLabel && ` · updated ${lastRefreshedLabel}`}
        </p>
        <button
          type="button"
          onClick={() => refetch()}
          disabled={isFetching}
          className="inline-flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-700 disabled:opacity-50"
        >
          <RefreshIcon
            sx={{ fontSize: 14 }}
            className={isFetching ? 'animate-spin' : undefined}
          />
          Refresh
        </button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loading />
        </div>
      ) : dayGroups.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-center text-sm text-gray-500">
          <InfoOutlinedIcon className="text-gray-300 mb-2" sx={{ fontSize: 40 }} />
          <p>{emptyLabel}</p>
        </div>
      ) : (
        <div className="pb-20 flex flex-col gap-3">
          {dayGroups.slice(0, displayedDayCount).map((group) => (
            <Accordion
              key={group.dateKey}
              defaultExpanded={group.dateKey === defaultExpandedKey}
              header={
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-sm font-semibold text-gray-900 truncate">
                    {group.dateHeader}
                  </span>
                  {group.hasOpenSession && (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[10px] font-semibold uppercase tracking-wide">
                      <PlayArrowIcon sx={{ fontSize: 10 }} />
                      In progress
                    </span>
                  )}
                </div>
              }
              rightLabel={formatMinutes(group.totalMinutes)}
            >
              <div className="flex flex-col gap-2">
                {group.sessions.map((session) => (
                  <PunchSessionCard
                    key={session.enterPunchClockId}
                    session={session}
                    adjustment={
                      adjustmentByEnterId.get(session.enterPunchClockId) ?? null
                    }
                    canEdit={canEdit}
                    onEdit={() => setEditSession(session)}
                  />
                ))}
              </div>
            </Accordion>
          ))}

          {dayGroups.length > displayedDayCount && (
            <button
              type="button"
              onClick={() => setDisplayedDayCount((c) => c + DAYS_PAGE_SIZE)}
              className="mt-1 w-full flex flex-col items-center gap-0.5 py-3 px-4 rounded-lg bg-indigo-50 hover:bg-indigo-100 text-indigo-700"
            >
              <span className="text-sm font-semibold">Load more days</span>
              <span className="text-[11px] text-gray-500">
                Showing {displayedDayCount} of {dayGroups.length}
              </span>
            </button>
          )}
        </div>
      )}

      {editSession && (
        <EditSessionExitModal
          isOpen={!!editSession}
          onClose={() => setEditSession(null)}
          enterPunchClockId={editSession.enterPunchClockId}
          currentEnter={editSession.from}
          rawEnter={editSession.rawFrom}
          currentExit={editSession.to}
          rawExit={editSession.rawTo}
          isOpenSession={editSession.isOpen}
          locationName={editSession.locationName}
          sessionDate={editSession.from}
          existingAdjustment={adjustmentByEnterId.get(
            editSession.enterPunchClockId,
          )}
        />
      )}
    </SliderModal>
  );
};

export default UserPunchHistoryDrawer;
